import Icon, { Loading3QuartersOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import PauseSvg from '@/components/Icon/svgs/audio_pause.svg';
import PlaySvg from '@/components/Icon/svgs/audio_play.svg';
import WarningSvg from '@/components/Icon/svgs/fill-warning.svg';
import RetrySvg from '@/components/Icon/svgs/retry.svg';
import TalkVoiceSvg from '@/components/Icon/svgs/talk_voice.svg';
import {
  type IBriefIntroduction,
  generateBriefIntroduction,
  getBriefIntroduction,
  saveBriefIntroduction
} from '@/service/visualization';
import useAudio from '@/ui/components/memory-detail/components/attachments/components/AudioPreview/useAudio';
import { formatDurationV2 } from '@/utils/timestamp-format';

import styles from './index.less';

interface IProps {
  data?: IBriefIntroduction;
  className?: string;
  handleSaveCb: (data: IBriefIntroduction) => void;
}

const BriefIntroduction = (props: IProps) => {
  const { data, className, handleSaveCb } = props;

  const [briefIntroduction, setBriefIntroduction] = useState<IBriefIntroduction | undefined>(data);

  const [inputValue, setInputValue] = useState<string>(data?.briefIntroduction || '');

  const isChanged = useMemo(
    () => inputValue !== (briefIntroduction?.briefIntroduction || ''),
    [inputValue, briefIntroduction]
  );

  const [generatingWord, setGeneratingWord] = useState<boolean>(false);
  const [generatingVoice, setGeneratingVoice] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleGetBriefIntroduction = () => {
    getBriefIntroduction()
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        if (res.data.data.briefIntroduction.status === 'doing') {
          setTimeout(() => {
            handleGetBriefIntroduction();
          }, 2000);
          handleSaveCb(res.data.data.briefIntroduction);
        } else if (res.data.data.briefIntroduction.status === 'error') {
          throw new Error(res.data.data.briefIntroduction.errorMessage);
        } else {
          handleSaveCb(res.data.data.briefIntroduction);
          setBriefIntroduction(res.data.data.briefIntroduction);
          res.data.data.briefIntroduction.briefIntroduction
            ? setInputValue(res.data.data.briefIntroduction.briefIntroduction)
            : null;
          setGeneratingVoice(false);
        }
      })
      .catch((e) => {
        setError(e.message);
        setGeneratingVoice(false);
      });
  };

  const handleRegenerateWord = () => {
    setGeneratingWord(true);
    setError(null);

    generateBriefIntroduction()
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        setInputValue(res.data.data || '');
      })
      .catch((e) => {
        setError(e.message);
        setGeneratingWord(false);
      })
      .finally(() => {
        setGeneratingWord(false);
      });
  };

  const handleSave = () => {
    if (saving || generatingWord || generatingVoice || !isChanged) return;

    setSaving(true);
    setGeneratingVoice(true);

    saveBriefIntroduction({
      briefIntroduction: inputValue
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }
      })
      .then(() => {
        handleGetBriefIntroduction();
      })
      .catch((e) => {
        setError(e.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const { isPlaying, handlePlayStatusChange, handleReplay, duration } = useAudio({
    link: briefIntroduction?.voiceUrl || ''
  });

  const handleGenerateFromEmpty = () => {
    setGeneratingVoice(true);
    setGeneratingWord(true);

    generateBriefIntroduction()
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        setInputValue(res.data.data || '');
        setGeneratingWord(false);
        setSaving(true);

        return saveBriefIntroduction({
          briefIntroduction: res.data.data || ''
        });
      })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        setSaving(false);
      })
      .then(() => {
        handleGetBriefIntroduction();
      })
      .catch((e) => {
        setError(e.message);
        setSaving(false);
        setGeneratingVoice(false);
        setGeneratingWord(false);
      });
  };

  useEffect(() => {
    if (data) {
      setBriefIntroduction(data);
      setInputValue(data.briefIntroduction || '');
    } else {
      handleGenerateFromEmpty();
    }
  }, [data]);

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.headerContainer}>
        <div
          className={classNames(styles.voiceContainer, {
            [styles.generating]: generatingVoice
          })}
          onClick={(e) => {
            e.stopPropagation();

            if (!briefIntroduction?.voiceUrl || isChanged || generatingVoice) {
              return;
            }

            if (isPlaying) {
              handlePlayStatusChange();
            } else {
              handleReplay();
            }
          }}
        >
          {briefIntroduction?.voiceUrl && !isChanged ? (
            <>
              <Icon
                className="text-[28px] text-primary-black"
                component={isPlaying ? PauseSvg : PlaySvg}
              />
              <Icon className="text-[28px] text-primary-black" component={TalkVoiceSvg} />
              <div className="ml-[6px] text-base leading-7 text-primary-black">
                {formatDurationV2((briefIntroduction.duration || duration || 0) * 1000)}
              </div>
            </>
          ) : (
            <>
              {generatingVoice ? (
                <Loading3QuartersOutlined className="text-[18px] p-[5px] text-primary-black" spin />
              ) : (
                <Icon className="text-[18px] p-[5px] text-primary-black" component={WarningSvg} />
              )}
              <Icon className="text-[28px] text-primary-black" component={TalkVoiceSvg} />
              <Icon
                className="text-[28px] text-primary-black -ml-[9.33px]"
                component={TalkVoiceSvg}
              />
            </>
          )}
        </div>
        {error || generatingVoice || isChanged ? (
          <div
            className={classNames(styles.tips, {
              [styles.error]: error,
              [styles.generating]: generatingVoice,
              [styles.changed]: isChanged
            })}
          >
            {error
              ? error
              : generatingVoice
                ? 'Generating...usually takes about 30s'
                : isChanged
                  ? 'Script changed. Save to regenerate.'
                  : ''}
          </div>
        ) : null}
      </div>

      <Input.TextArea
        autoSize={{ minRows: 8, maxRows: 14 }}
        className={styles.input}
        onChange={(e) => setInputValue(e.target.value)}
        value={inputValue}
      />

      {generatingWord ? (
        <div className="text-sm leading-6 text-[#756C5D] py-[2px] mt-[6px]">Generating...</div>
      ) : (
        <div
          className={classNames(styles.regenerateBtn, {
            [styles.disabled]: generatingVoice || generatingWord
          })}
          onClick={handleRegenerateWord}
        >
          <Icon className={styles.icon} component={RetrySvg} spin={generatingWord} />
          <div className={styles.text}>{'Regenerate'}</div>
        </div>
      )}

      <div
        className={classNames(styles.saveBtn, {
          [styles.disabled]: saving || generatingWord || generatingVoice || !isChanged
        })}
        onClick={handleSave}
      >
        {'Save'}
      </div>
    </div>
  );
};

export default BriefIntroduction;
